import { Box, Button, Flex, Heading, HTMLChakraProps, Icon, Link, Stack } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import LockIconOpen from '@/assets/icons/ic-lock-open.svg';
import LockIcon from '@/assets/icons/ic-lock.svg';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { DownloadLink } from '@/components/ui/features/lecture/LectureDownloads/components/DownloadLink';
import { LecturePremiumOverlayButton } from '@/components/ui/features/lecture/LecturePremiumOverlayButton/LecturePremiumOverlayButton';
import { PREMIUM_OVERLAY_ID } from '@/components/ui/features/originals/EpisodesSection/components/EpisodePremiumOverlayDesktop';
import { useAnalytics } from '@/context/Analytics';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { useLibLynx } from '@/hooks/useLibLynx';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { queryLectureAccessPermission } from '@/queries/lecture-access-permissions';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';
import { Transcript } from '@/resources/Transcript';
import { ILibLynxMetadata } from '@/types/LibLynx';

const compareAlphabeticly = (a: Transcript, b: Transcript) => a.language.name.localeCompare(b.language.name);

export const LectureDownloads: FC<HTMLChakraProps<'div'>> = (props) => {
  const { t } = useTranslation();
  const libLynx = useLibLynx();
  const analytics = useAnalytics();
  const { id: lectureId } = useLecturePageQueryParams();
  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));
  const { data: transcripts } = useResources(queryTranscriptsByLectureId(lectureId));

  const { data } = useResource(queryLectureAccessPermission(lectureId));

  const isTranscriptAvailable = lecture?.playlist && transcripts?.length > 0;

  const isDownloadsVisible = lecture?.slideshowUrl || lecture?.paperUrl || lecture?.paperLink || isTranscriptAvailable;

  const sortedTranscripts = useMemo(
    () =>
      transcripts
        ?.slice()
        .sort(compareAlphabeticly)
        .filter((transcript) => transcript.pdfUrl),
    [transcripts]
  );

  const downloadPaperAnylitics = (url: string) => {
    analytics.track('library_lecture_paper_download', {
      lecture_name: lecture.title,
      speaker_name: lecture.sortedProfiles.map((speaker) => speaker.fullName),
      event_id: lecture.eventId,
      lecture_id: lecture.id,
    });

    const libLynxMetadata: ILibLynxMetadata = {
      dtype: 'multimedia',
      id: lecture.doi || lectureId,
      title: `${lecture.title} - [download paper]`,
      uri: url,
    };

    libLynx.send('trackItemRequest', { im: libLynxMetadata });
  };

  const downloadSlidesAnylitics = (url: string) => {
    analytics.track('library_lecture_slides_download', {
      lecture_name: lecture.title,
      speaker_name: lecture.sortedProfiles.map((speaker) => speaker.fullName),
      event_id: lecture.eventId,
      lecture_id: lecture.id,
    });

    const libLynxMetadata: ILibLynxMetadata = {
      dtype: 'multimedia',
      id: lecture.doi || lectureId,
      title: `${lecture.title} - [download slides]`,
      uri: url,
    };

    libLynx.send('trackItemRequest', { im: libLynxMetadata });
  };

  const downloadTranscriptAnalytics = (language: string) => {
    const libLynxMetadata: ILibLynxMetadata = {
      dtype: 'multimedia',
      id: lecture.doi || lectureId,
      title: `${lecture.title} - [download transcript (${language})]`,
    };

    libLynx.send('trackItemRequest', { im: libLynxMetadata });
  };

  if (!isDownloadsVisible) {
    return null;
  }

  return (
    <Box {...props} overflowX={{ base: 'scroll', md: 'initial' }}>
      <Heading fontSize="xl" lineHeight={7} color="gray.900" mb={7}>
        <Flex alignItems="center">
          {data?.restricted && <Icon mr="2" as={LockIcon} h="1.5rem" w="1.5rem" color="rgb(239,80,133)" />}
          {t('newLecture.downloadsTitle')}
        </Flex>
      </Heading>
      <Box position="relative">
        <Stack w="100%" flexWrap="wrap" direction={{ base: 'row', md: 'column' }} spacing={{ base: 4, md: 3 }}>
          {lecture?.slideshowUrl && (
            <DownloadLink onClick={() => downloadSlidesAnylitics(lecture?.slideshowUrl)} href={lecture?.slideshowUrl}>
              {t('newLecture.downloadSlides')}
            </DownloadLink>
          )}
          {lecture?.paperUrl && (
            <DownloadLink onClick={() => downloadPaperAnylitics(lecture?.paperUrl)} href={lecture?.paperUrl}>
              {t('newLecture.downloadPaper')}
            </DownloadLink>
          )}
          {lecture?.paperLink && (
            <DownloadLink onClick={() => downloadPaperAnylitics(lecture?.paperLink)} href={lecture?.paperLink}>
              {t('newLecture.downloadPaper')}
            </DownloadLink>
          )}
          {isTranscriptAvailable &&
            sortedTranscripts?.map((transcript) => (
              <DownloadLink
                onClick={() => downloadTranscriptAnalytics(transcript.language.name)}
                key={transcript.id}
                href={transcript.pdfUrl}
              >
                {t('lecture.transcript', {
                  name: transcript.language.name,
                })}
              </DownloadLink>
            ))}
        </Stack>
        {data?.restricted && (
          <Flex
            justifyContent="center"
            alignItems="center"
            position="absolute"
            h="full"
            w="full"
            bgColor="rgba(243, 245, 245, 0.9)"
            top={0}
            left={0}
          >
            <Link hideBelow="lg" href={`#${PREMIUM_OVERLAY_ID}`}>
              <Button
                rightIcon={<Icon as={LockIconOpen} h="1.5rem" w="1.5rem" color="white" />}
                as="button"
                variant="solid"
                size="lg"
              >
                {t('premiumContentFallback.accessPremiumContent')}
              </Button>
            </Link>
            <LecturePremiumOverlayButton hideFrom="lg" />
          </Flex>
        )}
      </Box>
    </Box>
  );
};
